import { sizeNameMap } from "./sizeNameUtils";

/**
 * Deduplicates listings by URL and title, aggregating size ranges
 * @param {Object} sizesData - The group.sizes object from the data model
 * @param {Object} sizesMetaData - The group.sizes_meta object with historical price data
 * @returns {Array} Array of deduplicated listings with size range info
 */
export function deduplicateListings(sizesData, sizesMetaData) {
  const listingMap = new Map();

  Object.entries(sizesData).forEach(([size, listings]) => {
    const sizeMeta = sizesMetaData?.[size] || {};

    listings.forEach((listing) => {
      const key = `${listing.product_url}|${listing.title}`;

      var pricePerFish = listing.price / listing.number_of_individuals;
      const currentReference = Number(sizeMeta.current_comparison_price ?? sizeMeta.avg_price);
      const historicalReference = sizeMeta.has_historical_data ? Number(sizeMeta.historical_comparison_price) : null;
      const comparisonReference = historicalReference > 0 ? historicalReference : currentReference;
      var savingsAmount = comparisonReference - pricePerFish;
      var savingsPercentage = comparisonReference > 0
        ? (savingsAmount / comparisonReference) * 100 : 0;
      const currentSavingsPercentage = currentReference > 0
        ? ((currentReference - pricePerFish) / currentReference) * 100 : null;
      const historicalSavingsPercentage = historicalReference > 0
        ? ((historicalReference - pricePerFish) / historicalReference) * 100 : null;

      var numericSize = parseFloat(size);
      if (numericSize === -1) {
        numericSize = NaN;
      }

      if (!listingMap.has(key)) {
        listingMap.set(key, {
          ...listing,
          sizes: [size],
          sizeMin: isNaN(numericSize) ? null : numericSize,
          sizeMax: isNaN(numericSize) ? null : numericSize,
          nonNumericSizes: isNaN(numericSize) ? [size] : [],
          pricePerFish: pricePerFish,
          bestSavingsPercentage: savingsPercentage,
          bestSavingsAmount: savingsAmount,
          currentSavingsPercentage,
          historicalSavingsPercentage,
          currentComparisonPrice: Number.isFinite(currentReference) ? currentReference : null,
          historicalComparisonPrice:
            historicalReference,
        });
      } else {
        const existing = listingMap.get(key);
        existing.sizes.push(size);

        if (!isNaN(numericSize)) {
          if (existing.sizeMin === null || numericSize < existing.sizeMin) {
            existing.sizeMin = numericSize;
          }
          if (existing.sizeMax === null || numericSize > existing.sizeMax) {
            existing.sizeMax = numericSize;
          }
        } else {
          if (!existing.nonNumericSizes.includes(size)) {
            existing.nonNumericSizes.push(size);
          }
        }
      }
    });
  });

  return Array.from(listingMap.values());
}

/**
 * Formats size range for display
 * @param {Object} listing - Deduplicated listing with sizeMin, sizeMax, nonNumericSizes
 * @returns {string} Formatted size string
 */
export function formatSizeRange(listing) {
  const parts = [];

  if (listing.sizeMin !== null && listing.sizeMax !== null) {
    if (listing.sizeMin === listing.sizeMax) {
      parts.push(`${listing.sizeMin}"`);
    } else {
      parts.push(`${listing.sizeMin}-${listing.sizeMax}"`);
    }
  }

  listing.nonNumericSizes?.forEach((size) => {
    // Handle -1 or "-1" as Unknown
    if (size === -1 || size === "-1") {
      parts.push("Unknown");
    } else {
      const displayName = sizeNameMap[size] || size;
      if (displayName !== "Unknown Size") {
        parts.push(displayName);
      }
    }
  });

  if (parts.length === 0) {
    return "Unknown Size";
  }

  return parts.join(", ");
}

/**
 * Deduplicates a flat array of listings by product_url+title, merging size ranges.
 * Unlike deduplicateListings which expects {size: [listings]} objects,
 * this works with a plain array of listing objects that each have a .size property.
 * @param {Array} listings - Flat array of listing objects
 * @returns {Array} Array of deduplicated listings with sizeMin, sizeMax, nonNumericSizes, pricePerFish
 */
export function deduplicateListingArray(listings) {
  const map = new Map();
  listings.forEach((listing) => {
    const key = `${listing.product_url}|${listing.title}`;
    let numericSize = parseFloat(listing.size);
    if (numericSize === -1) numericSize = NaN;

    if (!map.has(key)) {
      map.set(key, {
        ...listing,
        sizes: [listing.size],
        sizeMin: isNaN(numericSize) ? null : numericSize,
        sizeMax: isNaN(numericSize) ? null : numericSize,
        nonNumericSizes: isNaN(numericSize) ? [listing.size] : [],
        pricePerFish: listing.price / (listing.number_of_individuals || 1),
      });
    } else {
      const existing = map.get(key);
      if (!existing.sizes.includes(listing.size)) {
        existing.sizes.push(listing.size);
      }
      if (!isNaN(numericSize)) {
        if (existing.sizeMin === null || numericSize < existing.sizeMin) existing.sizeMin = numericSize;
        if (existing.sizeMax === null || numericSize > existing.sizeMax) existing.sizeMax = numericSize;
      } else if (!existing.nonNumericSizes.includes(listing.size)) {
        existing.nonNumericSizes.push(listing.size);
      }
    }
  });
  return Array.from(map.values());
}

/**
 * Gets the best price per size from listings data
 * @param {Object} sizesData - The group.sizes object
 * @param {Object} sizesMetaData - The group.sizes_meta object
 * @returns {Array} Array of { size, bestPrice, listing, savingsPercentage, historicalPrice }
 */
export function getBestPricesPerSize(sizesData, sizesMetaData) {
  return Object.entries(sizesData)
    .map(([size, listings]) => {
      if (!listings || listings.length === 0) return null;

      const sortedListings = [...listings].sort(
        (a, b) =>
          a.price / a.number_of_individuals - b.price / b.number_of_individuals
      );

      const bestListing = sortedListings[0];
      const sizeMeta = sizesMetaData?.[size] || {};

      return {
        size,
        bestPrice: bestListing.price / bestListing.number_of_individuals,
        listing: bestListing,
        savingsPercentage: Number(sizeMeta.max_savings_percentage) || 0,
        savingsAmount: Number(sizeMeta.max_savings) || 0,
        historicalPrice: sizeMeta.historical_comparison_price || null,
        listingCount: listings.length,
      };
    })
    .filter(Boolean);
}

/**
 * Gets the best deals (listings with highest savings percentage)
 * @param {Array} deduplicatedListings - Output from deduplicateListings
 * @param {number} limit - Maximum number of deals to return
 * @returns {Array} Top deals sorted by savings percentage
 */
export function getBestDeals(deduplicatedListings, limit = 4) {
  return [...deduplicatedListings]
    .sort((a, b) => b.bestSavingsPercentage - a.bestSavingsPercentage)
    .slice(0, limit);
}

/**
 * Check if a listing matches a size filter
 * @param {Object} listing - Deduplicated listing
 * @param {Array} numericSizeRange - [min, max] for numeric sizes
 * @param {Array} selectedNonNumericSizes - Selected non-numeric sizes (empty array = all non-numeric)
 * @param {boolean} includeNumeric - Whether to include numeric sizes
 * @param {boolean} includeNonNumeric - Whether to include non-numeric sizes
 * @returns {boolean} Whether the listing matches the filter
 */
export function listingMatchesSizeFilter(
  listing,
  numericSizeRange,
  selectedNonNumericSizes,
  includeNumeric = true,
  includeNonNumeric = true
) {
  const hasNumericSizes = listing.sizeMin !== null && listing.sizeMax !== null;
  const hasNonNumericSizes =
    listing.nonNumericSizes && listing.nonNumericSizes.length > 0;

  // Check numeric size match
  if (
    includeNumeric &&
    numericSizeRange &&
    hasNumericSizes &&
    listing.sizeMax >= numericSizeRange[0] &&
    listing.sizeMin <= numericSizeRange[1]
  ) {
    return true;
  }

  // Check non-numeric size match (including Unknown/-1 sizes)
  if (includeNonNumeric && hasNonNumericSizes) {
    // Check if any of the listing's non-numeric sizes are selected
    let matchesNonNumeric = listing.nonNumericSizes.some((size) => {
      const displayName = size === "-1" ? "Unknown" : sizeNameMap[size] || size;
      return (
        selectedNonNumericSizes.includes(size) ||
        selectedNonNumericSizes.includes(displayName)
      );
    });
    if (matchesNonNumeric) {
      return true;
    }
  }

  return false;
}
